import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2Off } from 'lucide-react';
import { AuthPageLayout } from '../components/AuthPageLayout';

export function ExpiredAuthLinkPage() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/resend-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const payload = (await res.json().catch(() => null)) as
        | { message?: string; error?: string; retry_after_seconds?: number }
        | null;
      if (!res.ok) {
        throw new Error(payload?.error ?? 'Einladung konnte nicht erneut gesendet werden.');
      }
      setSuccessMessage(payload?.message ?? 'Ein neuer Einladungslink wurde gesendet.');
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Einladung konnte nicht erneut gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      {sent ? (
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left gap-4">
          <div className="w-14 h-14 rounded-full bg-green-50 border border-green-100 flex items-center justify-center">
            <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">E-Mail gesendet</h1>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              {successMessage}
            </p>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Verwende nur den Link aus der <strong className="font-medium text-slate-800">neuesten</strong> E-Mail.
            </p>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              Bereits registriert?{' '}
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="text-[#3b82f6] hover:underline font-medium"
              >
                Zum Login
              </button>
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left gap-4">
            <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center">
              <Link2Off className="w-7 h-7 text-red-500" aria-hidden />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Link abgelaufen</h1>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Dieser Einladungslink ist nicht mehr gültig. Du kannst dir unten einen neuen Link
                an deine E-Mail-Adresse schicken lassen.
              </p>
            </div>
          </div>

          <form onSubmit={(e) => { void handleSubmit(e); }} className="mt-6 space-y-4">
            <label className="block">
              <span className="block text-xs font-medium text-slate-600 mb-1.5">E-Mail-Adresse</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                placeholder="name@firma.de"
              />
            </label>

            {error ? (
              <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm"
            >
              {loading ? 'Link wird gesendet…' : 'Neuen Einladungslink senden'}
            </button>

            <p className="text-center text-sm text-slate-500">
              Bereits registriert?{' '}
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="text-[#3b82f6] hover:underline font-medium"
              >
                Zum Login
              </button>
            </p>
          </form>
        </>
      )}

      <button
        type="button"
        onClick={() => navigate('/login', { replace: true })}
        className="w-full mt-4 text-sm text-[#3b82f6] hover:underline"
      >
        Zurück zum Login
      </button>
    </AuthPageLayout>
  );
}
